/**
 * SettingsModal — TAL-1
 *
 * Reusable modal overlay for all configuration panels.
 * Opens from gear icon in Command Center (admin) or Workbench (tenant).
 * Left sidebar nav + content area, matching Writers Factory UX.
 * Panel list tracks the canonical /settings route via settingsModalPanelIdToPath().
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsProvider } from '@seitiate/settings-ui';
import { SettingsSidebar, type SettingsPanel } from './SettingsSidebar';
import { TalentsPanel } from './panels/TalentsPanel';
import { ConnectorsPanel } from './panels/ConnectorsPanel';
import { SeityPanel } from './panels/AlterEgoPanel';
import { VoicePanel } from './panels/VoicePanel';
import { GovernorPanel } from './panels/GovernorPanel';
import { DataMgmtPanel } from './panels/DataMgmtPanel';
import { TrustBondPanel } from './panels/TrustBondPanel';
import { NotificationSettingsPanel } from './panels/NotificationSettingsPanel';
import { MemoryLegibilityPanel } from './panels/MemoryLegibilityPanel';
import { settingsModalPanelIdToPath } from '@/components/settings/settingsSpineConfig';

const TenantPassthrough =
  (Inner: React.ComponentType): React.FC<{ mode: 'admin' | 'tenant' }> =>
  () =>
    <Inner />;

// The old Models & Providers cockpit left the menu with the Models & Routing
// section (DOCKET-2026-10-20-05): model, key, routing and spend settings live
// at /settings/models and its sibling sections, never in this modal.
const PANELS: SettingsPanel[] = [
  { id: 'alter-ego', label: 'Seity', icon: '👤', adminOnly: false, component: SeityPanel },
  { id: 'trust', label: 'Bond', icon: '🛡', adminOnly: false, component: TenantPassthrough(TrustBondPanel) },
  { id: 'voice', label: 'Voice', icon: '🎙', adminOnly: false, component: VoicePanel },
  { id: 'standards', label: 'Governor', icon: '⚙', adminOnly: false, component: GovernorPanel },
  { id: 'connectors', label: 'Connectors', icon: '🔌', adminOnly: false, component: ConnectorsPanel },
  { id: 'talents', label: 'Talents', icon: '⚡', adminOnly: false, component: TalentsPanel },
  { id: 'memory', label: 'Memory', icon: '🧭', adminOnly: false, component: TenantPassthrough(MemoryLegibilityPanel) },
  {
    id: 'notifications',
    label: 'Alerts',
    icon: '🔔',
    adminOnly: false,
    component: TenantPassthrough(NotificationSettingsPanel),
  },
  { id: 'data-mgmt', label: 'Data', icon: '💾', adminOnly: false, component: DataMgmtPanel },
];

interface SettingsModalProps {
  mode: 'admin' | 'tenant';
  initialPanel?: string;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ mode, initialPanel = 'talents', onClose }) => {
  const [activePanel, setActivePanel] = useState(initialPanel);
  const navigate = useNavigate();

  const visiblePanels = PANELS.filter((p) => mode === 'admin' || !p.adminOnly);

  const activePanelDef = visiblePanels.find((p) => p.id === activePanel) || visiblePanels[0];
  const PanelComponent = activePanelDef?.component || PANELS[0].component;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <SettingsProvider variant="creator" role={mode}>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative w-[90vw] max-w-[1200px] h-[80vh] bg-[#0d0d0d] border border-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-800">
          <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-gray-300">
            Settings
            {mode === 'admin' && (
              <span className="ml-2 text-[10px] text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded">DIRECTOR</span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors text-lg leading-none"
            aria-label="Close settings"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <SettingsSidebar panels={visiblePanels} activePanel={activePanel} onSelectPanel={setActivePanel} />

          <div className="flex-1 overflow-y-auto p-6">
            <PanelComponent mode={mode} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 px-6 py-3 border-t border-gray-800 bg-gray-900/30">
          <ContextualHelp panelId={activePanel} mode={mode} />
          {mode === 'tenant' ? (
            <button
              type="button"
              onClick={() => {
                navigate(settingsModalPanelIdToPath(activePanel));
                onClose();
              }}
              className="text-[11px] font-mono text-[#39FF14] hover:underline"
            >
              Open full settings page
            </button>
          ) : null}
        </div>
      </div>
    </div>
    </SettingsProvider>
  );
};

const ContextualHelp: React.FC<{ panelId: string; mode: 'admin' | 'tenant' }> = ({ panelId, mode }) => {
  const helpText: Record<string, string> = {
    talents:
      mode === 'admin'
        ? 'Talents update automatically as skills are built. Wiring and Planned items are admin-only launch planning data.'
        : "Connected sources feed your seity's understanding of your creative voice. Upgrade to unlock gated capabilities.",
    connectors: 'Connectors link external services to your workspace. Connected sources provide context for AI-assisted creation.',
    voice: 'Voice calibration fine-tunes how the AI writes in your style. Higher injection levels mean stronger voice influence.',
    'alter-ego': 'The name, personality, and agency level of your assistant shape every interaction.',
    trust: 'Bond and trust summary is read-first: posture and signals you can share, not operator certification consoles.',
    advanced: 'Machine room controls tune the governance engine and default pipeline behavior for new work.',
    'data-mgmt': 'Export your data, clear local caches, or reset selected settings. Reset actions cannot be undone.',
    memory: 'Memory legibility surfaces mirror readiness and Codex-facing signals without exposing operator internals.',
    notifications: 'Delivery preferences are stored via the notifications API—the same data as the full settings route.',
  };

  return (
    <p className="text-[11px] text-gray-500 font-mono">
      ℹ {helpText[panelId] || 'Configure this panel to customize your experience.'}
    </p>
  );
};
